import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { t } from "ttag";

import { ErrorModal } from "@/app/components/legacy-modals";
import { referralQueriesStore } from "@/features/referral/api";
import {
  ReferralCodeBanner,
  ReferralStats,
} from "@/features/referral/components";
import { useApplyReferralCreditAmount } from "@/features/referral/hooks";
import type { CreditAmount } from "@/features/referral/models";
import {
  CreditOptions,
  HowDoesItWorkModal,
} from "@/features/referral/sections";
import { LogoLoader } from "@/shared/components";
import { REFERRAL_CREDIT_AMOUNT } from "@/shared/domains/referral";
import { useBoolean } from "@/shared/hooks";
import type { ModalProps } from "@/shared/models/legacy-types";
import {
  Button,
  Card,
  Dialog,
  DollarMinimalisticIcon,
  LightbulbIcon,
} from "@/shared/ui";

export const ReferralDashboardModal = ({ show, onClose }: ModalProps) => {
  const [selectedAmount, setSelectedAmount] = useState<CreditAmount>();

  const {
    data: credit,
    isLoading,
    isError,
    isSuccess,
  } = useQuery(referralQueriesStore.credit);

  const {
    value: showHowDoesItWorksModal,
    setTrue: openHowDoesItWorksModal,
    setFalse: closeHowDoesItWorksModal,
  } = useBoolean(false);

  const applyCreditAmountMutation = useApplyReferralCreditAmount();

  const canApplyCreditAmount =
    isSuccess && credit.available > 0 && !credit.hasReachedMonthlyLimit;

  const handleApply = () => {
    if (selectedAmount) {
      applyCreditAmountMutation.mutate(selectedAmount, { onSuccess: onClose });
    }
  };

  if (isError) {
    return <ErrorModal open={show} onOpenChange={onClose} />;
  }

  return (
    <>
      <Dialog.Root open={show} onOpenChange={onClose}>
        <LogoLoader
          show={(isLoading || applyCreditAmountMutation.isPending) && show}
          fullscreen
        />

        {isSuccess && (
          <Dialog.Content>
            <div className="flex flex-col gap-6">
              <Dialog.Header icon={LightbulbIcon}>
                <Dialog.Title>{t`Referral dashboard`}</Dialog.Title>
                <Dialog.Description>
                  {t`Give $${REFERRAL_CREDIT_AMOUNT}, get $${REFERRAL_CREDIT_AMOUNT}! Invite friends & family to join LevelsRx!`}
                  <br />
                  {t`See how it works`}{" "}
                  <button
                    onClick={openHowDoesItWorksModal}
                    type="button"
                    className="font-semibold underline"
                  >
                    {t`here`}
                  </button>
                  .
                </Dialog.Description>
              </Dialog.Header>

              <ReferralCodeBanner />
            </div>

            <div className="flex flex-col gap-6">
              <Card.Root ghost className="gap-4">
                <Card.Title>
                  <DollarMinimalisticIcon className="size-4" />
                  {t`Referral summary`}
                </Card.Title>

                <ReferralStats credit={credit} />
              </Card.Root>

              <CreditOptions
                credit={credit}
                value={selectedAmount}
                onChange={setSelectedAmount}
              />
            </div>

            {canApplyCreditAmount && (
              <Dialog.Footer className="z-20">
                <Button
                  size="lg"
                  disabled={!selectedAmount}
                  onClick={handleApply}
                  className="grow"
                >
                  {t`Apply`}
                </Button>
              </Dialog.Footer>
            )}
          </Dialog.Content>
        )}
      </Dialog.Root>

      <HowDoesItWorkModal
        show={showHowDoesItWorksModal}
        onClose={closeHowDoesItWorksModal}
      />
    </>
  );
};
